import moment from 'moment';
import { useState, useEffect } from 'react';
import { useTranslation } from '~i18n';

function prefixInteger(num: number, n: number = 2) {
  if (num >= Math.pow(10, n - 1)) {
    return num.toString();
  }
  return (Array(n).join('0') + num).slice(-n);
}

export function amendmentCountDown(startTime: any, endTime: any) {
  const du = moment.duration(endTime - startTime, 'ms');
  const days = du.get('days'),
    hours = du.get('hours'),
    mins = du.get('minutes'),
    ss = du.get('seconds');
  if (days <= 0 && hours <= 0 && mins <= 0 && ss <= 0) {
    return 0;
  } else if (days > 0) {
    return `${days} days ${prefixInteger(hours)}h ${prefixInteger(mins)}min`;
  } else if (hours > 0) {
    return `${prefixInteger(hours)}h ${prefixInteger(mins)}min`;
  } else if (mins > 0) {
    return `${prefixInteger(mins)}min`;
  } else {
    return `${prefixInteger(ss)}s`;
  }
}

function getDurationFromNow(
  deadline: moment.Moment,
  durationUnit: moment.DurationInputArg2 = 'ms'
) {
  return moment.duration(deadline.valueOf() - moment().valueOf(), durationUnit);
}

function useDeadlineDuration(deadline: moment.Moment, interval: number = 1000): moment.Duration {
  const [duration, setDuration] = useState<moment.Duration>(getDurationFromNow(deadline));

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDuration(getDurationFromNow(deadline));
    }, interval);

    if (getDurationFromNow(deadline).asMilliseconds() <= 0) {
      clearTimeout(timerId);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [duration, deadline, interval]);

  return duration;
}

export function useCountdownText(deadline: moment.Moment, interval: number = 1000) {
  const { t } = useTranslation('public');
  const duration = useDeadlineDuration(deadline, interval);

  const days = Math.floor(duration.asDays()),
    hours = duration.hours(),
    minutes = duration.minutes(),
    seconds = duration.seconds();

  const daysText = days > 0 ? `${days}${t('day', { count: days })}` : '';
  const hoursText = hours > 0 ? `${hours}h` : '';
  const minutesText = minutes > 0 ? `${minutes}min` : '';
  const secondsText = days <= 0 && seconds >= 0 ? `${seconds}s` : '';

  return [daysText, hoursText, minutesText, secondsText].join(' ');
}
